import api from './api.service';

export type SalesAnalyticsTab =
  | 'overview'
  | 'presales'
  | 'orders'
  | 'invoicing'
  | 'collections'
  | 'customers'
  | 'team';

export type SalesAnalyticsFunnelMode = 'volume' | 'cohort';
export type SalesAnalyticsCompareTo = 'prior_period' | 'prior_year';

export interface SalesAnalyticsFilterParams {
  date_from?: string;
  date_to?: string;
  sales_team_id?: string;
  customer_id?: string;
  revenue_service_type_id?: string;
  industry?: string;
  country?: string;
  funnel_mode?: SalesAnalyticsFunnelMode;
  compare_to?: SalesAnalyticsCompareTo;
}

export interface SalesAnalyticsKpiComparison {
  current: number;
  prior: number;
  change_pct: number | null;
  change_abs: number;
}

export interface SalesAnalyticsExecutiveInsight {
  level: 'info' | 'warning' | 'positive';
  message: string;
}

export interface FunnelStageRow {
  stage: string;
  count: number;
  value: number;
  conversion_pct_from_prior: number | null;
}

export interface SalesAnalyticsOverview {
  filters: { date_from: string; date_to: string };
  funnel_mode: SalesAnalyticsFunnelMode;
  kpis: {
    invoiced_revenue: number;
    booked_so_value: number;
    open_pipeline_value: number;
    collections_in_period: number;
    active_customers: number;
    avg_invoice_value: number;
    invoice_count: number;
    open_backlog_value: number;
    open_backlog_count: number;
  };
  kpi_comparison?: {
    invoiced_revenue?: SalesAnalyticsKpiComparison;
    booked_so_value?: SalesAnalyticsKpiComparison;
    collections_in_period?: SalesAnalyticsKpiComparison;
  };
  executive_insights: SalesAnalyticsExecutiveInsight[];
  funnel: FunnelStageRow[];
  revenue_trend: { monthLabel: string; yyyymm: string; revenue: number }[];
}

export interface SalesAnalyticsFilterOptions {
  industries: string[];
  countries: string[];
}

export interface SalesAnalyticsPresalesReport {
  lead_stats: {
    total: number;
    qualified: number;
    converted: number;
    qualification_ratio_pct: number;
  };
  opportunity_pipeline: {
    open_count: number;
    pipeline_value: number;
    weighted_value: number;
  };
  quotation_summary: {
    pipeline_count: number;
    pipeline_value: number;
    won: number;
    lost: number;
    open: number;
    win_rate_pct: number | null;
  };
  pipeline_by_industry: { industry: string; count: number; value: number }[];
  stale_opportunities: Array<{
    id: string;
    title: string;
    customer_name: string | null;
    estimated_value: number;
    link_path: string;
  }>;
  expiring_quotations: Array<{
    id: string;
    quotation_number: string;
    customer_name: string | null;
    grand_total: number;
    valid_until: string | null;
    link_path: string;
  }>;
  unconverted_quotations: Array<{
    id: string;
    quotation_number: string;
    status: string;
    grand_total: number;
    quotation_date: string;
    customer_name: string | null;
    link_path: string;
    commercial_stage?: 'awaiting_customer_order' | 'won_pr_awaiting_so';
    commercial_stage_label?: string;
    days_since_accepted?: number;
    owner_name?: string | null;
    has_active_production_request?: boolean;
    acceptance_date?: string | null;
  }>;
}

export interface SalesAnalyticsOrdersReport {
  open_backlog: { order_count: number; open_value: number };
  deliveries_posted: { count: number; value: number };
  delivered_not_invoiced: {
    estimated_net_value: number;
    lines_with_availability: number;
    rows: Array<{
      id: string;
      delivery_note_number: string;
      delivery_date: string;
      customer_name: string | null;
      link_path: string;
    }>;
  };
  orders_at_risk: Array<{
    sales_order_id: string;
    sales_order_number: string;
    customer_name: string;
    required_date: string;
    readiness_status: string;
    link_path: string;
  }>;
}

export interface SalesAnalyticsInvoicingReport {
  invoiced_total: number;
  invoice_count: number;
  ar_aging_buckets: { bucket: string; amount: number; count: number }[];
  overdue_customers: Array<{
    customer_id: string;
    customer_name: string;
    total_overdue: number;
    total_outstanding: number;
  }>;
  invoices: Array<{
    invoice_id: string;
    invoice_number: string;
    customer_name: string;
    invoice_date: string;
    due_date: string;
    grand_total: number;
    balance_due: number;
    link_path: string;
  }>;
}

export interface SalesAnalyticsCollectionsReport {
  collections_total: number;
  invoiced_total: number;
  collection_rate_pct: number | null;
  open_ar_remaining: number;
  receipts: Array<{
    receipt_voucher_id: string;
    voucher_number: string;
    receipt_date: string;
    customer_name: string | null;
    amount: number;
    allocated_amount: number;
    unallocated_amount: number;
    link_path: string;
  }>;
}

export interface SalesAnalyticsCustomersReport {
  top_customers: Array<{
    customer_id: string;
    customer_name: string;
    revenue: number;
    invoice_count: number;
    link_path: string;
  }>;
  revenue_by_country: Array<{ country: string; revenue: number; customer_count: number }>;
  pipeline_by_industry: Array<{ industry: string; count: number; value: number }>;
}

export interface SalesAnalyticsTeamReport {
  teams: Array<{
    team_id: string;
    team_name: string;
    leads: number;
    opportunities: number;
    quotations: number;
    booked_orders: number;
    booked_value: number;
    invoiced: number;
    collected: number;
  }>;
}

function unwrap<T>(res: unknown): T {
  const r = res as { data?: { data?: T } | T };
  return (r?.data as { data?: T })?.data ?? (r?.data as T) ?? (res as T);
}

function queryParams(filters: SalesAnalyticsFilterParams): Record<string, string> {
  const p: Record<string, string> = {};
  if (filters.date_from) p.date_from = filters.date_from;
  if (filters.date_to) p.date_to = filters.date_to;
  if (filters.sales_team_id) p.sales_team_id = filters.sales_team_id;
  if (filters.customer_id) p.customer_id = filters.customer_id;
  if (filters.revenue_service_type_id) p.revenue_service_type_id = filters.revenue_service_type_id;
  if (filters.industry) p.industry = filters.industry;
  if (filters.country) p.country = filters.country;
  if (filters.funnel_mode) p.funnel_mode = filters.funnel_mode;
  if (filters.compare_to) p.compare_to = filters.compare_to;
  return p;
}

export async function getSalesAnalyticsFilterOptions(): Promise<SalesAnalyticsFilterOptions> {
  const res = await api.get('/sales/analytics/filter-options');
  return unwrap<SalesAnalyticsFilterOptions>(res);
}

export async function getSalesAnalyticsOverview(
  filters: SalesAnalyticsFilterParams
): Promise<SalesAnalyticsOverview> {
  const res = await api.get('/sales/analytics/overview', { params: queryParams(filters) });
  return unwrap<SalesAnalyticsOverview>(res);
}

export async function getSalesAnalyticsFunnel(filters: SalesAnalyticsFilterParams): Promise<FunnelStageRow[]> {
  const res = await api.get('/sales/analytics/funnel', { params: queryParams(filters) });
  const data = unwrap<FunnelStageRow[]>(res);
  return Array.isArray(data) ? data : [];
}

export async function getSalesAnalyticsRevenueTrend(filters: SalesAnalyticsFilterParams) {
  const res = await api.get('/sales/analytics/revenue-trend', { params: queryParams(filters) });
  const data = unwrap<{ monthLabel: string; yyyymm: string; revenue: number }[]>(res);
  return Array.isArray(data) ? data : [];
}

export async function getSalesAnalyticsPresales(
  filters: SalesAnalyticsFilterParams
): Promise<SalesAnalyticsPresalesReport> {
  const res = await api.get('/sales/analytics/presales', { params: queryParams(filters) });
  return unwrap<SalesAnalyticsPresalesReport>(res);
}

export async function getSalesAnalyticsOrders(
  filters: SalesAnalyticsFilterParams
): Promise<SalesAnalyticsOrdersReport> {
  const res = await api.get('/sales/analytics/orders-fulfillment', { params: queryParams(filters) });
  return unwrap<SalesAnalyticsOrdersReport>(res);
}

export async function getSalesAnalyticsInvoicing(
  filters: SalesAnalyticsFilterParams
): Promise<SalesAnalyticsInvoicingReport> {
  const res = await api.get('/sales/analytics/invoicing-ar', { params: queryParams(filters) });
  return unwrap<SalesAnalyticsInvoicingReport>(res);
}

export async function getSalesAnalyticsCollections(
  filters: SalesAnalyticsFilterParams
): Promise<SalesAnalyticsCollectionsReport> {
  const res = await api.get('/sales/analytics/collections', { params: queryParams(filters) });
  return unwrap<SalesAnalyticsCollectionsReport>(res);
}

export async function getSalesAnalyticsCustomers(
  filters: SalesAnalyticsFilterParams
): Promise<SalesAnalyticsCustomersReport> {
  const res = await api.get('/sales/analytics/customers', { params: queryParams(filters) });
  return unwrap<SalesAnalyticsCustomersReport>(res);
}

export async function getSalesAnalyticsTeam(
  filters: SalesAnalyticsFilterParams
): Promise<SalesAnalyticsTeamReport> {
  const res = await api.get('/sales/analytics/team-performance', { params: queryParams(filters) });
  return unwrap<SalesAnalyticsTeamReport>(res);
}

export async function downloadSalesAnalyticsExport(
  tab: SalesAnalyticsTab,
  filters: SalesAnalyticsFilterParams,
  format: 'csv' | 'xlsx' = 'xlsx'
): Promise<void> {
  const res = await api.get('/sales/analytics/export', {
    params: { ...queryParams(filters), tab, format },
    responseType: 'blob',
  });
  const blob = res.data as Blob;
  const ext = format === 'csv' ? 'csv' : 'xlsx';
  const filename = `sales-analytics-${tab}-${filters.date_from ?? ''}_${filters.date_to ?? ''}.${ext}`;
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function defaultAnalyticsDateRange(): { date_from: string; date_to: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { date_from: fmt(start), date_to: fmt(end) };
}

export type AnalyticsPeriodPreset = 'mtd' | 'qtd' | 'ytd' | 'last30' | 'last90';

export function analyticsDateRangeForPreset(preset: AnalyticsPeriodPreset): { date_from: string; date_to: string } {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const end = fmt(today);
  switch (preset) {
    case 'mtd':
      return { date_from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), date_to: end };
    case 'qtd': {
      const qStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
      return { date_from: fmt(qStart), date_to: end };
    }
    case 'ytd':
      return { date_from: fmt(new Date(today.getFullYear(), 0, 1)), date_to: end };
    case 'last30': {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { date_from: fmt(d), date_to: end };
    }
    case 'last90': {
      const d = new Date(today);
      d.setDate(d.getDate() - 89);
      return { date_from: fmt(d), date_to: end };
    }
    default:
      return defaultAnalyticsDateRange();
  }
}

export function parseAnalyticsFiltersFromSearchParams(
  searchParams: URLSearchParams
): SalesAnalyticsFilterParams {
  const defaults = defaultAnalyticsDateRange();
  const get = (k: string) => searchParams.get(k) || undefined;
  const funnelRaw = get('funnel_mode');
  const compareRaw = get('compare_to');
  return {
    date_from: get('date_from') ?? defaults.date_from,
    date_to: get('date_to') ?? defaults.date_to,
    sales_team_id: get('sales_team_id'),
    customer_id: get('customer_id'),
    revenue_service_type_id: get('revenue_service_type_id'),
    industry: get('industry'),
    country: get('country'),
    funnel_mode: funnelRaw === 'cohort' ? 'cohort' : funnelRaw === 'volume' ? 'volume' : undefined,
    compare_to:
      compareRaw === 'prior_period' || compareRaw === 'prior_year' ? compareRaw : undefined,
  };
}

export function filtersQueryKey(filters: SalesAnalyticsFilterParams): string {
  return JSON.stringify(filters);
}

/** Build Operations tab URL with shared filter context for bridge links. */
export function operationsBridgeUrl(
  section: 'overdue-ar' | 'delivery-queues' | 'orders-at-risk' | 'crm-followups',
  filters: SalesAnalyticsFilterParams
): string {
  const params = new URLSearchParams({ tab: 'operations' });
  if (filters.sales_team_id) params.set('sales_team_id', filters.sales_team_id);
  if (filters.customer_id) params.set('customer_id', filters.customer_id);
  params.set('section', section);
  return `/sales/dashboard?${params.toString()}#${section}`;
}

/** Build Owner Intelligence presales deep link with shared filters. */
export function ownerIntelligencePresalesUrl(filters: SalesAnalyticsFilterParams): string {
  const params = new URLSearchParams();
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  if (filters.sales_team_id) params.set('sales_team_id', filters.sales_team_id);
  if (filters.industry) params.set('industry', filters.industry);
  const q = params.toString();
  return `/owner-intelligence/sales-pipeline-intelligence${q ? `?${q}` : ''}`;
}
